import * as React from 'react';
import type { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { FormField } from '@ooh/ui/components/form-field';
import { Input } from '@ooh/ui/components/input';
import {
  SearchableSelectField as Select,
  type SearchableSelectFieldOption as SelectOption,
} from '@ooh/ui/components/searchable-select-field';
import { Separator } from '@ooh/ui/components/separator';
import type { FormValues } from '../../../../lib/media-unit-form-schema';
import { MATERIAL_VALUES, LIGHTING_VALUES } from '@ooh/contracts';

interface TechnicalSectionProps {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  formatOptions: SelectOption[];
  computedArea: string;
  t: TFunction<'inventory'>;
  shouldDirty?: boolean;
  /** False for media types the import doesn't require dimensions for (e.g. cityscroll). */
  isDimensionRequired: boolean;
}

export function TechnicalSection({
  register,
  errors,
  watch,
  setValue,
  formatOptions,
  computedArea,
  t,
  shouldDirty,
  isDimensionRequired,
}: TechnicalSectionProps) {
  const opts = shouldDirty ? { shouldDirty: true } : undefined;

  return (
    <Card data-testid="section-technical">
      <CardHeader className="pb-4">
        <CardTitle className="text-[15px]">{t('form.sections.technical')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FormField
            label={t('form.fields.format')}
            required
            error={errors.formatId?.message}
            testId="field-format"
          >
            <Select
              options={formatOptions}
              value={watch('formatId') ?? ''}
              onChange={(v) => setValue('formatId', v, opts)}
              placeholder={t('form.placeholders.format')}
              error={!!errors.formatId}
              data-testid="select-format"
            />
          </FormField>

          <FormField
            label={t('form.fields.material')}
            error={errors.material?.message}
            testId="field-material"
          >
            <Select
              options={MATERIAL_VALUES.map((v) => ({ value: v, label: v }))}
              value={watch('material') ?? ''}
              onChange={(v) => setValue('material', v as (typeof MATERIAL_VALUES)[number], opts)}
              placeholder={t('form.placeholders.material')}
              allowEmpty
              emptyLabel={t('form.placeholders.clearSelection')}
              data-testid="select-material"
            />
          </FormField>

          <FormField
            label={t('form.fields.lighting')}
            error={errors.lighting?.message}
            testId="field-lighting"
          >
            <Select
              options={LIGHTING_VALUES.map((v) => ({ value: v, label: v }))}
              value={watch('lighting') ?? ''}
              onChange={(v) => setValue('lighting', v as (typeof LIGHTING_VALUES)[number], opts)}
              placeholder={t('form.placeholders.lighting')}
              allowEmpty
              emptyLabel={t('form.placeholders.clearSelection')}
              data-testid="select-lighting"
            />
          </FormField>
        </div>

        <Separator />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField
            label={t('form.fields.width')}
            required={isDimensionRequired}
            error={errors.widthM?.message}
            testId="field-widthM"
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder={t('form.placeholders.width')}
              error={!!errors.widthM}
              data-testid="input-widthM"
              {...register('widthM')}
            />
          </FormField>

          <FormField
            label={t('form.fields.height')}
            required={isDimensionRequired}
            error={errors.heightM?.message}
            testId="field-heightM"
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder={t('form.placeholders.height')}
              error={!!errors.heightM}
              data-testid="input-heightM"
              {...register('heightM')}
            />
          </FormField>

          <FormField label={t('form.fields.area')} testId="field-area">
            <Input
              value={computedArea}
              disabled
              placeholder={t('form.placeholders.area')}
              data-testid="input-area"
              readOnly
            />
          </FormField>
        </div>
      </CardContent>
    </Card>
  );
}
